// src/pages/CourseList.tsx - LISTADO DE CAPACITACIONES
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, BarChart, Users, Calendar, Monitor } from 'lucide-react';
import { courses } from '../data/courses';
import { CourseCategory, CourseLevel } from '../types';
import { parseLocalDate } from '../lib/utils';
import Reveal from '../components/ui/Reveal';

const SELECT_CLASS =
  'w-full p-3 border border-white/20 rounded-lg bg-infinity-black/40 text-infinity-white focus:ring-2 focus:ring-infinity-yellow focus:border-transparent hover:border-infinity-yellow/60 transition-colors duration-200';

const CourseList: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<CourseCategory | 'all'>('all');
  const [selectedLevel, setSelectedLevel] = useState<CourseLevel | 'all'>('all');
  const [selectedModality, setSelectedModality] = useState<'presencial' | 'online' | 'hybrid' | 'all'>('all');

  const categories = [
    { value: 'all', label: 'Todas las categorías' },
    { value: 'energias-renovables', label: 'Energías Renovables' },
    { value: 'electricidad', label: 'Electricidad Básica' },
    { value: 'domotica', label: 'Domótica' },
    { value: 'instalaciones', label: 'Instalaciones' }
  ];

  const levels = [
    { value: 'all', label: 'Todos los niveles' },
    { value: 'basico', label: 'Básico' },
    { value: 'intermedio', label: 'Intermedio' },
    { value: 'avanzado', label: 'Avanzado' },
    { value: 'especializado', label: 'Especializado' }
  ];

  const modalities = [
    { value: 'all', label: 'Todas las modalidades' },
    { value: 'presencial', label: 'Presencial' },
    { value: 'online', label: 'Online' },
    { value: 'hybrid', label: 'Híbrido' }
  ];

  const filteredCourses = courses.filter(course => {
    const matchesCategory = selectedCategory === 'all' || course.category === selectedCategory;
    const matchesLevel = selectedLevel === 'all' || course.level === selectedLevel;
    const matchesModality = selectedModality === 'all' || course.modality === selectedModality;

    return matchesCategory && matchesLevel && matchesModality;
  });

  const getModalityIcon = (modality: string) => {
    switch (modality) {
      case 'presencial':
        return <Users size={16} className="text-infinity-purple" />;
      case 'online':
        return <Monitor size={16} className="text-infinity-yellow" />;
      case 'hybrid':
        return <Calendar size={16} className="text-infinity-blue" />;
      default:
        return null;
    }
  };

  const getLevelColor = (level: CourseLevel) => {
    switch (level) {
      case 'basico':
        return 'bg-infinity-blue/20 text-infinity-blue';
      case 'intermedio':
        return 'bg-infinity-yellow/20 text-infinity-yellow';
      case 'avanzado':
        return 'bg-infinity-pink/20 text-infinity-pink';
      default:
        return 'bg-infinity-purple/20 text-infinity-violet';
    }
  };

  return (
    <div className="min-h-screen bg-infinity-black">
      {/* Hero Section */}
      <section className="bg-infinity-gradient text-infinity-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
            Capacitaciones Técnicas
          </h1>
          <p className="text-xl opacity-0 animate-fade-in-up" style={{ animationDelay: '250ms' }}>
            Desarrolla tus habilidades con nuestros cursos especializados
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filtros */}
        <Reveal className="glass rounded-lg shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-infinity-white mb-2">
                Categoría
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as any)}
                className={SELECT_CLASS}
              >
                {categories.map(category => (
                  <option key={category.value} value={category.value} className="bg-infinity-black">
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-infinity-white mb-2">
                Nivel
              </label>
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value as any)}
                className={SELECT_CLASS}
              >
                {levels.map(level => (
                  <option key={level.value} value={level.value} className="bg-infinity-black">
                    {level.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-infinity-white mb-2">
                Modalidad
              </label>
              <select
                value={selectedModality}
                onChange={(e) => setSelectedModality(e.target.value as any)}
                className={SELECT_CLASS}
              >
                {modalities.map(modality => (
                  <option key={modality.value} value={modality.value} className="bg-infinity-black">
                    {modality.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Reveal>

        {/* Lista de Cursos */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {filteredCourses.map((course, index) => (
            <Reveal key={course.id} delay={index * 80}>
              <div className="group bg-infinity-dark rounded-lg shadow-lg overflow-hidden card-hover border border-transparent hover:border-infinity-yellow/40 h-full">
                <div className="overflow-hidden">
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-auto transition-transform duration-500 group-hover:scale-105"
                  />
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className={`px-3 py-1 text-xs font-medium rounded-full transition-transform duration-300 group-hover:scale-105 ${getLevelColor(course.level)}`}
                    >
                      {course.level.charAt(0).toUpperCase() + course.level.slice(1)}
                    </span>

                    <div className="flex items-center space-x-1">
                      {getModalityIcon(course.modality)}
                      <span className="text-sm text-white capitalize">{course.modality}</span>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-infinity-yellow mb-3">{course.title}</h3>
                  <p className="text-infinity-white/80 mb-4">{course.description}</p>

                  <div className="flex items-center space-x-6 mb-6 text-sm text-infinity-white/60">
                    <div className="flex items-center space-x-1">
                      <Clock size={16} />
                      <span>{course.duration}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <BarChart size={16} />
                      <span>{course.level}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Users size={16} />
                      <span>Certificado</span>
                    </div>
                  </div>

                  {course.startDate && (
                    <div className="flex items-center space-x-1 mb-4 text-sm text-infinity-yellow">
                      <Calendar size={16} />
                      <span>Inicia: {parseLocalDate(course.startDate).toLocaleDateString('es-PE')}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <Link
                      to={`/capacitaciones/${course.id}`}
                      className="btn-primary bg-infinity-yellow text-infinity-black hover:bg-infinity-purple hover:text-infinity-white hover:scale-105 hover:shadow-glow transition-all duration-300"
                    >
                      Ver Detalles
                    </Link>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {filteredCourses.length === 0 && (
          <Reveal className="text-center py-12">
            <p className="text-infinity-white/60 text-lg">No se encontraron capacitaciones con los filtros seleccionados</p>
          </Reveal>
        )}
      </div>
    </div>
  );
};

export default CourseList;
